import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Monitor } from 'lucide-react'

import { api, cajaWebElegida, elegirCajaWeb } from '@/lib/api'
import { config } from '@/lib/config'
import { LookupSelect } from '@/componentes/ui/LookupSelect'

interface CajaWeb {
  id: number
  nombre: string
  prefijo: string
}

/**
 * En qué caja está parada esta pestaña.
 *
 * Aparece SOLO en el navegador y SOLO si el local tiene más de una caja web.
 * Los dos «solo» importan: en la caja del mostrador la caja es la máquina y
 * preguntarlo no tiene sentido, y en el local que trabaja con una sola caja web
 * este selector sería un campo más en la pantalla que se mira ocho horas por
 * día, con una única opción.
 *
 * Existe porque un local puede querer trabajar todo en línea con dos personas
 * cobrando: dos mostradores con dos tablets son dos cajas, cada una con su
 * turno y su arqueo. Sin esto, la plata de las dos caía en el mismo arqueo y
 * solo una podía abrir turno.
 */
export function SelectorCaja({ compacto }: { compacto?: boolean }) {
  const qc = useQueryClient()
  const esCaja = config().runtime === 'caja'

  const { data = [] } = useQuery({
    queryKey: ['pos', 'cajas-web'],
    queryFn: () => api.get<CajaWeb[]>('/puntos-de-venta/web'),

    // En la caja del mostrador no se pregunta: la caja es esta máquina.
    enabled: !esCaja,

    // La lista cambia cuando alguien agrega una caja en Configuración, no
    // mientras se atiende.
    staleTime: 5 * 60_000,
    retry: false,
  })

  const elegida = cajaWebElegida()

  // Si la caja guardada ya no está (la dieron de baja, o este navegador venía
  // con una de otro local), se limpia: mejor caer en la de fábrica que seguir
  // mandando un id que el servidor va a rechazar en el peor momento.
  useEffect(() => {
    if (elegida !== null && data.length > 0 && !data.some((c) => c.id === elegida)) {
      elegirCajaWeb(null)
      void qc.invalidateQueries({ queryKey: ['pos'] })
    }
  }, [data, elegida, qc])

  if (esCaja || data.length < 2) return null

  const cambiar = (id: number | null) => {
    elegirCajaWeb(id)

    // El turno, el arqueo y las ventas del día son de LA CAJA, así que al
    // cambiarla hay que soltar todo lo cacheado: si no, la pantalla sigue
    // mostrando el turno de la caja anterior y el cajero cobra creyendo que
    // está en la otra.
    void qc.invalidateQueries({ queryKey: ['pos'] })
    void qc.invalidateQueries({ queryKey: ['turno'] })
  }

  const actual = data.find((c) => c.id === elegida) ?? data[0]

  return (
    <div className={compacto ? 'w-[200px] shrink-0' : 'max-w-sm'}>
      {!compacto && (
        <label className="mb-1 flex items-center gap-1.5 text-[13px] font-medium text-tinta-suave">
          <Monitor className="h-3.5 w-3.5 text-tinta-tenue" />
          En qué caja estás
        </label>
      )}

      <LookupSelect
        items={data.map((c) => ({
          id: c.id,
          code: c.prefijo,
          nombre: c.nombre,

          // El detalle solo en la pantalla del turno: en la barra del POS el
          // combo lo muestra al lado del nombre y le come el ancho al campo de
          // código, que es EL campo de esa pantalla —ahí escribe el lector—.
          detalle: compacto ? null : `Los tickets salen como ${c.prefijo}-000001`,
        }))}
        valor={actual?.id ?? null}
        onCambio={(id) => cambiar(id)}
        placeholder="Elegí la caja"
      />

      {!compacto && (
        <p className="mt-1.5 text-[12px] text-tinta-tenue">
          Cada caja tiene su propio turno y su propio arqueo. Queda guardado en este navegador.
        </p>
      )}
    </div>
  )
}
